import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth';
import { getCompanyAdminList, getCompanyList, getVesselImageUrl, removeVesselImage, replaceVesselImage, updateVessel } from '../core/_requests';
import { Company, CompanyAdmin } from '../core/_models';
import { toast } from 'react-toastify';
import { KTSVG } from '../../../../_metronic/helpers';
import {
  actorCanPickAnyCompanyGroup,
  actorUsesOwnCompanyGroupScope,
  resolveEffectiveCompanyGroupId,
} from '../core/adminActorContext';
import {VESSEL_TYPE_OPTIONS} from '../core/vesselTypeOptions';
import VesselContactListField from './VesselContactListField';
import {
  addContactEntry,
  buildContactEntries,
  getPrimaryContactValue,
  removeContactEntry,
  serializeContactEntries,
  setPrimaryContact,
  updateContactValue,
  validateContactEntries,
} from '../core/vesselContactFields';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import PortSelect from '../../../shared/components/PortSelect'
import { getApiErrorMessage, showErrorToast } from '../../../shared/utils/apiErrorMessage'

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onVesselUpdated: () => void;
  vesselData: any; // Ideally, replace 'any' with a proper Vessel interface
}

const EditVesselModal: React.FC<Props> = ({ onClose, isOpen, onVesselUpdated, vesselData }) => {
  const [selectedCompany, setSelectedCompany] = useState<{ id: number; name: string }>({ id: 0, name: '' });
  const [selectedCompanyAdmin, setSelectedCompanyAdmin] = useState<{ id: number; name: string }>({ id: 0, name: '' });

  const [vesselName, setVesselName] = useState('');
  const [vesselInitials, setVesselInitials] = useState('');
  const [imoNumber, setImoNumber] = useState('');
  const [officialNumber, setOfficialNumber] = useState('');
  const [mmsi, setMmsi] = useState('');
  const [callSign, setCallSign] = useState('');
  const [naCallSign, setNaCallSign] = useState(false);
  const [flag, setFlag] = useState('');
  const [portOfRegistry, setPortOfRegistry] = useState('');
  const [vesselClass, setVesselClass] = useState('');
  const [classNotation, setClassNotation] = useState('');
  const [piClub, setPiClub] = useState('');
  const [vesselArea, setVesselArea] = useState('');
  const [dwt, setDwt] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [vesselPhoneNumbers, setVesselPhoneNumbers] = useState(buildContactEntries());
  const [vesselEmails, setVesselEmails] = useState(buildContactEntries());

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [companiesbyAdmin, setCompaniesByAdmin] = useState<Company[]>([]);
  const [originalCompanyId, setOriginalCompanyId] = useState<number>(0);
  const [originalCompanyAdminId, setOriginalCompanyAdminId] = useState<number>(0);
  const [vesselImageFile, setVesselImageFile] = useState<File | null>(null);
  const [vesselImageError, setVesselImageError] = useState<string | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);

  const { currentUser } = useAuth()
  const actorCanPickCompanyGroup = actorCanPickAnyCompanyGroup(currentUser)
  const actorOwnCompanyGroupScope = actorUsesOwnCompanyGroupScope(currentUser)
  const effectiveCgaId = resolveEffectiveCompanyGroupId(currentUser)
  useEffect(() => {
    if (isOpen && vesselData) {
      console.log(vesselData);
      setVesselName(vesselData.fleet_name || '');
      setVesselInitials(vesselData.vesselInitials || '');
      setImoNumber(vesselData.imoNumber || '');
      setOfficialNumber(vesselData.officialNumber || '');
      setMmsi(vesselData.mmsi || '');
      const savedCallSign = vesselData.call_sign || '';
      const isNA = savedCallSign.trim().toUpperCase() === 'N/A';
      setNaCallSign(isNA);
      setCallSign(isNA ? 'N/A' : savedCallSign);
      setFlag(vesselData.flag || '');
      setPortOfRegistry(vesselData.portOfRegistry || '');
      setVesselClass(vesselData.classes || '');
      setClassNotation(vesselData.classNotation || '');
      setPiClub(vesselData.piClub || '');
      setVesselArea(vesselData.area || '');
      setDwt(vesselData.dwt || '');
      setVesselType(vesselData.vesselType || '');
      setVesselPhoneNumbers(
        buildContactEntries(vesselData.vesselPhoneNumbers, vesselData.vesselPhoneNumber)
      );
      setVesselEmails(
        buildContactEntries(vesselData.vesselEmails, vesselData.vesselEmailAddress)
      );

      const companyId = vesselData.companyAdminId;
      const adminId = vesselData.companyGroupAdminId;

      setOriginalCompanyId(companyId);
      setOriginalCompanyAdminId(adminId);
      setVesselImageFile(null);
      setVesselImageError(null);
      setRemoveCurrentImage(false);

      setSelectedCompanyAdmin({ id: vesselData.companyGroupAdmin.id, name: vesselData.companyGroupAdmin.name });
      setSelectedCompany({ id: vesselData.companyAdmin?.id, name: vesselData.companyAdmin?.name });

      // if (vesselData.companyAdmin) {
      //   setSelectedCompany({ id: vesselData.companyAdmin.id, name: vesselData.companyAdmin.name });
      // } else {
      //   setSelectedCompany({ id: 0, name: '' });
      // }

      // if (vesselData.companyGroupAdmin) {
      //   setSelectedCompanyAdmin({ id: vesselData.companyGroupAdmin.id, name: vesselData.companyGroupAdmin.name });
      // } else {
      //   setSelectedCompanyAdmin({ id: 0, name: '' });
      // }
      fetchCompaniesByAdmin(adminId);
      getVesselImageUrl(vesselData.id).then(setCurrentImageUrl).catch(() => setCurrentImageUrl(null));
    }
  }, [isOpen, vesselData]);

  useEffect(() => {
  // If acting as CGA (CGA or Operator-CGA), lock CGA and fetch its sub-companies list
  if (actorOwnCompanyGroupScope && effectiveCgaId) {
    setSelectedCompanyAdmin(prev => prev.id ? prev : { id: effectiveCgaId, name: '' });
  }

  fetchCompanies();               // will adapt based on actorOwnCompanyGroupScope/effectiveCgaId

  // Only Superadmin (or Operator-Superadmin) needs CGA list
  if (actorCanPickCompanyGroup) {
    fetchCompanyAdmins();
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [actorOwnCompanyGroupScope, actorCanPickCompanyGroup, effectiveCgaId]);

  useEffect(() => {
    if (selectedCompanyAdmin.id) {
      fetchCompaniesByAdmin(selectedCompanyAdmin.id);
    } else {
      setCompaniesByAdmin([]);
    }
  }, [selectedCompanyAdmin.id]);

  const fetchCompanies = async () => {
  try {
    const companyList = await getCompanyList();
    console.log('Company List:', companyList);

    // When acting as CGA (CGA or Operator-CGA), show only sub-companies under the effective CGA.
    // Otherwise (Superadmin), this list is not used in the UI; keep it empty to avoid confusion.
    const activeCompanies = actorOwnCompanyGroupScope && effectiveCgaId
      ? companyList.filter(c => c.active === true && c.cga?.id === Number(effectiveCgaId))
      : [];

    setCompanies(activeCompanies);
  } catch (error) {
    console.error('Failed to fetch company list:', error);
  }
};

  const fetchCompaniesByAdmin = async (adminId: number) => {
    try {
      const companyList = await getCompanyList();
      const filteredCompanies = companyList.filter(c => c.cga?.id === adminId);
      console.log("Company by admins: ", filteredCompanies);
      setCompaniesByAdmin(filteredCompanies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };


  const fetchCompanyAdmins = async () => {
    try {
      const list = await getCompanyAdminList();
      setCompanyAdmins(list);
    } catch (err) {
      console.error('Failed to fetch company admins:', err);
    }
  };

  const validateVesselForm = () => {
    const errors: Record<string, string> = {};

    const hasImo = Boolean(imoNumber && imoNumber.trim());
    const hasOfficialNumber = Boolean(officialNumber && officialNumber.trim());

    if (!hasImo && !hasOfficialNumber) {
      errors.imoNumber = "Either IMO Number or Official Number is required.";
      errors.officialNumber = "Either IMO Number or Official Number is required.";
    } else if (hasImo && !/^\d{7}$/.test(imoNumber.trim())) {
      errors.imoNumber = "IMO Number must be a 7-digit number.";
    }

    if (!naCallSign && (!callSign || !/^[a-zA-Z0-9]{5,10}$/.test(callSign))) {
      errors.callSign = "Call Sign must be between 5 and 10 alphanumeric characters.";
    }

    if (!vesselName.trim()) {
      errors.vesselName = "Vessel Name is required.";
    }

    if (!mmsi || !/^\d{9}$/.test(mmsi)) {
      errors.vesselMMSI = "MMSI must be a 9-digit number.";
    }

    if (!flag.trim()) {
      errors.vesselFlag = "Vessel Flag is required.";
    }

    if (!vesselClass.trim()) {
      errors.vesselClass = "Vessel Class is required.";
    }

    if (!vesselArea.trim()) {
      errors.vesselArea = "Vessel Area is required.";
    }

    if (!dwt || +dwt <= 0) {
      errors.dwt = "DWT must be a positive number.";
    }

    if (!vesselType) {
      errors.vesselType = "Vessel Type is required.";
    }
    const phoneError = validateContactEntries(vesselPhoneNumbers, 'phone')
    if (phoneError) {
      errors.vesselPhoneNumber = phoneError
    }
    const emailError = validateContactEntries(vesselEmails, 'email')
    if (emailError) {
      errors.vesselEmailAddress = emailError
    }
    if (actorCanPickCompanyGroup) {
  if (selectedCompanyAdmin.id === 0) {
    errors.company = "Company is required.";
  }
}
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateVessel = async () => {
    if (!validateVesselForm() || loading) return;
    setLoading(true);
    setError(null);

    try {
      const isCompanyChanged =
        selectedCompany.id !== originalCompanyId ||
        selectedCompanyAdmin.id !== originalCompanyAdminId;

      // if (isCompanyChanged) {
      //   // 1. Deactivate existing vessel
      //   await updateVessel(auth.jwt, vesselData.id,
      //     vesselName,
      //     vesselType,
      //     imoNumber,
      //     mmsi,
      //     callSign,
      //     flag,
      //     vesselClass,
      //     vesselArea,
      //     dwt,
      //     originalCompanyId,
      //     originalCompanyAdminId,
      //     false // <- false = deactivate
      //   );

      //   await createVessel(
      //     auth.jwt,
      //     vesselName,
      //     vesselType,
      //     imoNumber,
      //     mmsi,
      //     callSign,
      //     flag,
      //     vesselClass,
      //     vesselArea,
      //     dwt,
      //     selectedCompany.id,
      //     selectedCompanyAdmin.id,
      //   )

      // }

      const finalCompanyAdminId = actorOwnCompanyGroupScope
  ? Number(effectiveCgaId)                  // lock to acting CGA
  : selectedCompanyAdmin.id;                // Superadmin picks in UI

      const finalCompanyId = selectedCompany.id;  // sub-company can be changed by CGA or Superadmin
      const serializedPhoneNumbers = serializeContactEntries(vesselPhoneNumbers)
      const serializedEmails = serializeContactEntries(vesselEmails)

await updateVessel(
  vesselData.id,
  vesselName,
  vesselType,
  imoNumber,
  officialNumber,
  mmsi,
  callSign,
  flag,
  portOfRegistry,
  vesselClass,
  classNotation,
  piClub,
  vesselArea,
  dwt,
  getPrimaryContactValue(vesselPhoneNumbers),
  getPrimaryContactValue(vesselEmails),
  serializedPhoneNumbers,
  serializedEmails,
  finalCompanyId,
  finalCompanyAdminId,
  vesselData.active,
  undefined,
  vesselInitials,
);

      if (removeCurrentImage) {
        try {
          await removeVesselImage(vesselData.id);
        } catch (imgRemErr) {
          console.error('Failed to remove vessel image:', imgRemErr);
          toast.warning('Vessel updated, but failed to remove existing image.');
        }
      }
      if (vesselImageFile) {
        try {
          await replaceVesselImage(vesselData.id, vesselImageFile);
        } catch (imageError) {
          console.error('Failed to upload vessel image:', imageError);
          toast.warning('Vessel updated, but image upload failed.');
        }
      }

      toast.success("Vessel updated successfully");

      onVesselUpdated();
      onClose();
    } catch (err) {
      console.error('Failed to update vessel:', err);
      const message = getApiErrorMessage(err, 'Failed to update vessel.', 'update');
      setError(message);
      toast.error(message, { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '60rem' }}>
        <div className="custom-modal-header d-flex justify-content-between align-items-center">
          <h5 className="m-0">Edit Vessel</h5>
          <button className="close-btn" onClick={onClose} disabled={loading}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className="custom-modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <div className="row">
            {actorOwnCompanyGroupScope && (
  <div className="col-md-6 mb-3">
    <label className="modal_label">Select Sub Company</label>
    <SearchableSelect
      className="form-control"
      value={selectedCompany?.id}
      onChange={(e) => {
        const id = +e.target.value;
        const obj = companies.find(c => c.id === id);
        setSelectedCompany(obj || { id: 0, name: '' });
      }}
    >
      <option value="">-- Select Sub Company --</option>
      {companies.filter(c => c.active === true).map(c => (
        <option key={c.id} value={c.id}>{c.name}</option>
      ))}
    </SearchableSelect>
  </div>
)}
            {actorCanPickCompanyGroup && (
  <>
    <div className="col-md-6 mb-3">
      <label className="modal_label">
        Select Company <span className="text-danger">*</span>
      </label>
      <SearchableSelect
        className="form-control"
        value={selectedCompanyAdmin?.id}
        onChange={(e) => {
          const id = +e.target.value;
          const obj = companyAdmins.find(c => c.id === id);
          setSelectedCompanyAdmin(obj || { id: 0, name: '' });
          setSelectedCompany({ id: 0, name: '' }); // Reset Sub Company on Company change
        }}
      >
        <option value="">-- Select Company --</option>
        {companyAdmins.filter(ca => ca.active === true).map(ca => (
          <option key={ca.id} value={ca.id}>{ca.name}</option>
        ))}
      </SearchableSelect>
      {fieldErrors.company && <small className="text-danger">{fieldErrors.company}</small>}
    </div>

    <div className="col-md-6 mb-3">
      <label className="modal_label">Select Sub Company</label>
      <SearchableSelect
        className="form-control"
        value={selectedCompany?.id}
        onChange={(e) => {
          const id = +e.target.value;
          const obj = companiesbyAdmin.find(c => c.id === id);
          setSelectedCompany(obj || { id: 0, name: '' });
        }}
      >
        <option value="">-- Select Sub Company --</option>
        {companiesbyAdmin.filter(c => c.active === true).map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </SearchableSelect>
    </div>
  </>
)}

            <div className="col-md-6 mb-3">
              <label className="modal_label">Vessel Name <span className="text-danger">*</span></label>
              <input type="text" className="form-control" value={vesselName} onChange={e => setVesselName(e.target.value)} />
              {fieldErrors.vesselName && <small className="text-danger">{fieldErrors.vesselName}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">Vessel Initials</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. MV"
                maxLength={20}
                value={vesselInitials}
                onChange={e => setVesselInitials(e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">IMO Number</label>
              <input type="number" className="form-control no-spinner" value={imoNumber} onChange={e => {
                setImoNumber(e.target.value);
              }} />
              {fieldErrors.imoNumber && <small className="text-danger">{fieldErrors.imoNumber}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">Official Number</label>
              <input type="text" className="form-control" value={officialNumber} onChange={e => setOfficialNumber(e.target.value)} />
              {fieldErrors.officialNumber && <small className="text-danger">{fieldErrors.officialNumber}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">Vessel MMSI <span className="text-danger">*</span></label>
              <input type="number" className="form-control no-spinner" value={mmsi} onChange={e => {
                setMmsi(e.target.value)
              }} />
              {fieldErrors.vesselMMSI && <small className="text-danger">{fieldErrors.vesselMMSI}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label d-flex align-items-center gap-2">
                Vessel CALL SIGN {!naCallSign && <span className="text-danger">*</span>}
                <span className="form-check d-flex align-items-center gap-1 ms-2">
                  <input
                    type="checkbox"
                    className="form-check-input me-1"
                    id="edit-vessel-call-sign-na"
                    checked={naCallSign}
                    onChange={() => {
                      const next = !naCallSign;
                      setNaCallSign(next);
                      setCallSign(next ? 'N/A' : '');
                    }}
                  />
                  <label className="form-check-label small" htmlFor="edit-vessel-call-sign-na">N/A</label>
                </span>
              </label>
              {naCallSign ? (
                <input type="text" className="form-control bg-light" value="N/A" disabled />
              ) : (
                <input type="text" className="form-control" value={callSign} onChange={e => setCallSign(e.target.value)} />
              )}
              {fieldErrors.callSign && <small className="text-danger">{fieldErrors.callSign}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">Vessel FLAG <span className="text-danger">*</span></label>
              <input type="text" className="form-control" value={flag} onChange={e => setFlag(e.target.value)} />
              {fieldErrors.vesselFlag && <small className="text-danger">{fieldErrors.vesselFlag}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">Port of Registry</label>
              <PortSelect
                value={portOfRegistry}
                onChange={e => setPortOfRegistry(e.target.value)}
                placeholder="Select or search port of registry..."
              />
            </div>
            <div className="col-md-12 mb-3">
              <label className="modal_label">Vessel CLASS <span className="text-danger">*</span></label>
              <textarea className="form-control" rows={3} value={vesselClass} onChange={e => setVesselClass(e.target.value)} />
              {fieldErrors.vesselClass && <small className="text-danger">{fieldErrors.vesselClass}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">Vessel Area (A1, A2, A3, A4) <span className="text-danger">*</span></label>
              <input type="text" className="form-control" value={vesselArea} onChange={e => setVesselArea(e.target.value)} />
              {fieldErrors.vesselArea && <small className="text-danger">{fieldErrors.vesselArea}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">Class Notation</label>
              <input type="text" className="form-control" value={classNotation} onChange={e => setClassNotation(e.target.value)} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">P&amp;I Club</label>
              <input type="text" className="form-control" value={piClub} onChange={e => setPiClub(e.target.value)} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="modal_label">DWT <span className="text-danger">*</span></label>
              <input type="number" className="form-control no-spinner" value={dwt} onChange={e => setDwt(e.target.value)} />
              {fieldErrors.dwt && <small className="text-danger">{fieldErrors.dwt}</small>}
            </div>
            <div className="col-md-6 mb-3">
              <VesselContactListField
                addLabel="Add Phone Number"
                entries={vesselPhoneNumbers}
                error={fieldErrors.vesselPhoneNumber}
                idPrefix="edit-vessel-phone"
                inputType="text"
                label="Vessel Phone Numbers"
                placeholder="Enter vessel phone number"
                onAdd={() => setVesselPhoneNumbers((current) => addContactEntry(current))}
                onChange={(index, value) => setVesselPhoneNumbers((current) => updateContactValue(current, index, value))}
                onMakePrimary={(index) => setVesselPhoneNumbers((current) => setPrimaryContact(current, index))}
                onRemove={(index) => setVesselPhoneNumbers((current) => removeContactEntry(current, index))}
              />
            </div>
            <div className="col-md-6 mb-3">
              <VesselContactListField
                addLabel="Add Email Address"
                entries={vesselEmails}
                error={fieldErrors.vesselEmailAddress}
                idPrefix="edit-vessel-email"
                inputType="email"
                label="Vessel Email Addresses"
                placeholder="Enter vessel email address"
                onAdd={() => setVesselEmails((current) => addContactEntry(current))}
                onChange={(index, value) => setVesselEmails((current) => updateContactValue(current, index, value))}
                onMakePrimary={(index) => setVesselEmails((current) => setPrimaryContact(current, index))}
                onRemove={(index) => setVesselEmails((current) => removeContactEntry(current, index))}
              />
            </div>
            <div className="col-md-12 mb-3">
              <label className="modal_label">Vessel Type <span className="text-danger">*</span></label>
              <SearchableSelect className="form-control" value={vesselType} onChange={(e) => {
                setVesselType(e.target.value)
              }}>
                <option value="">-- Select Vessel Type --</option>
                {VESSEL_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </SearchableSelect>
              {fieldErrors.vesselType && <small className="text-danger">{fieldErrors.vesselType}</small>}
            </div>

            <div className="col-md-12 mb-3">
              <label className="modal_label">Vessel Image</label>
              {currentImageUrl && !removeCurrentImage && (
                <div className="mb-2">
                  <img src={currentImageUrl} alt="Current vessel" style={{maxWidth: '220px', maxHeight: '140px', objectFit: 'cover'}} />
                </div>
              )}
              <input
                type="file"
                className="form-control"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (!file) {
                    setVesselImageFile(null);
                    setVesselImageError(null);
                    return;
                  }
                  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
                  const maxBytes = 5 * 1024 * 1024;
                  if (!allowedTypes.includes(file.type)) {
                    setVesselImageFile(null);
                    setVesselImageError('Only JPG, PNG, or WEBP images are allowed.');
                    return;
                  }
                  if (file.size > maxBytes) {
                    setVesselImageFile(null);
                    setVesselImageError('Image must be 5 MB or smaller.');
                    return;
                  }
                  setVesselImageFile(file);
                  setVesselImageError(null);
                }}
              />
              <small className="text-muted d-block mt-1">
                JPG, PNG, or WEBP. Max 5 MB.
              </small>
              {vesselImageFile && (
                <small className="text-success d-block mt-1">Selected: {vesselImageFile.name}</small>
              )}
              {vesselImageError && <small className="text-danger d-block mt-1">{vesselImageError}</small>}
              {currentImageUrl && (
                <div className="form-check mt-2">
                  <input
                    id="removeCurrentImage"
                    type="checkbox"
                    className="form-check-input"
                    checked={removeCurrentImage}
                    onChange={(e) => setRemoveCurrentImage(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="removeCurrentImage">Remove current image</label>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn_secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn_success d-flex align-items-center gap-2" onClick={handleUpdateVessel} disabled={loading}>
            {loading && <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />}
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditVesselModal;
